import React, { ForwardedRef, forwardRef, useCallback, useEffect, useState } from 'react'
import { Select, Typography } from 'antd'
import { DefaultOptionType, SelectValue } from 'antd/lib/select'
import cl from 'classnames'
import { BaseSelectRef } from 'rc-select'

import { Icon } from 'Shared/atoms/index'
import { usePopupContainer } from 'Shared/hooks/usePopupContainer'

import { Props } from './types'

import styles from './styles.module.scss'

const { Paragraph } = Typography

const MultiSelect = forwardRef<BaseSelectRef, Props<SelectValue>>(
	<T extends SelectValue>(
		{
			className,
			options,
			value,
			onChange,
			dataTestId,
			shouldFilterOptions = true,
			isReadOnly,
			...props
		}: React.PropsWithChildren<Props<T>> & {
			allowSelectTyping?: boolean
			isReadOnly?: boolean
		},
		ref: ForwardedRef<BaseSelectRef>
	) => {
		const [filteredOptions, setFilteredOptions] = useState<DefaultOptionType[]>(options || [])

		const handleChange = useCallback(
			(value: T, selectedItems: DefaultOptionType[] | DefaultOptionType) => {
				if (!!onChange) {
					onChange(value, selectedItems)
				}
			},
			[onChange]
		)

		useEffect(() => {
			if (!options) {
				return
			}
			let filteredOptions: DefaultOptionType[] = []
			//for multiselect filter!
			if (
				shouldFilterOptions &&
				(value as string[])?.every((val) => typeof val === 'string')
			) {
				filteredOptions = options?.filter(
					(option) => !(value as string[])?.find((item) => item === option.value)
				)
			} else {
				filteredOptions = options?.filter(
					(option) =>
						option.disabled ??
						!(value as LabelValueDisabled[])?.find(
							(item) => item.value === option.value
						)
				)
			}
			setFilteredOptions(filteredOptions)
		}, [options, shouldFilterOptions, value])

		const handleInputKeyDown = useCallback((event) => {
			event.preventDefault()
			event.stopPropagation()
		}, [])

		const getPopupContainer = usePopupContainer(dataTestId)

		return isReadOnly ? (
			<div className={styles.readOnly}>
				<Paragraph className={styles.paragraph}>
					{(value as LabelValue[])?.map(({ label }) => ` ${label}`).join(',')}
				</Paragraph>
			</div>
		) : (
			<Select<T>
				virtual={false}
				{...props}
				value={value}
				mode="multiple"
				onChange={handleChange}
				onInputKeyDown={props?.allowSelectTyping ? undefined : handleInputKeyDown}
				options={filteredOptions}
				popupClassName={cl(styles.basicMultiSelect, props.popupClassName)}
				className={cl(styles.basicMultiSelect, className)}
				suffixIcon={<Icon className={styles.suffixIcon} type="arrowDropdown" />}
				ref={ref}
				showSearch={false}
				getPopupContainer={getPopupContainer}
			/>
		)
	}
)

export default MultiSelect
